import React, { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { motion, useAnimation } from "framer-motion";
import { gql, useQuery } from "@apollo/client";
import Button from "components/Buttons";
import EmbedPost from "./HorsesAlbum";
import SaddlefitIcon from "assets/icons/instagram_header_logo.svg?react";
import InstagramIcon from "assets/images/instagram.svg";
import InstagramIconMobile from "assets/images/instagram.svg";
import { LIST_INSTAGRAM_POSTS } from "apollo/queries/instagramList";
import instagramList from "utils/instagram.json";
import "./style.css";

/** These URLs work in Graph/sync but typically 403 or abort in browsers after they expire. */
function isHotlinkedInstagramCdn(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const { hostname } = new URL(url);
    return (
      hostname.includes("cdninstagram.com") ||
      hostname.includes("fbcdn.net")
    );
  } catch {
    return false;
  }
}

function mediaNeedsCdnMerge(url) {
  return url ? isHotlinkedInstagramCdn(url) : false;
}

function postHasAnyHotlink(post) {
  return (
    mediaNeedsCdnMerge(post?.mediaUrl) ||
    mediaNeedsCdnMerge(post?.thumbnailUrl) ||
    mediaNeedsCdnMerge(post?.videoUrl)
  );
}

/** Keep AppSync counts; swap CDN URLs to bundled S3 (fallback JSON) when needed. */
function mergeStableInstagramMedia(api, fallback) {
  const posts = api?.media?.data;
  if (!posts?.length) return api;
  const fbItems = fallback?.media?.data ?? [];
  if (!fbItems.length) return api;

  const bySk = new Map(fbItems.map((p) => [String(p.sk ?? ""), p]));
  const byPermalink = new Map(fbItems.map((p) => [String(p.permalink ?? ""), p]));

  const data = posts.map((post) => {
    if (!postHasAnyHotlink(post)) return post;
    const sub =
      bySk.get(String(post.sk ?? "")) ||
      byPermalink.get(String(post.permalink ?? ""));
    if (!sub) return post;
    const next = { ...post };
    if (sub.mediaUrl && !isHotlinkedInstagramCdn(sub.mediaUrl)) {
      next.mediaUrl = sub.mediaUrl;
    }
    if (sub.thumbnailUrl && !isHotlinkedInstagramCdn(sub.thumbnailUrl)) {
      next.thumbnailUrl = sub.thumbnailUrl;
    }
    if (sub.videoUrl && !isHotlinkedInstagramCdn(sub.videoUrl)) {
      next.videoUrl = sub.videoUrl;
    }
    return next;
  });

  const allBad = data.every((p) => postHasAnyHotlink(p));
  if (allBad) {
    // Keep API order and posts; do not swap in static instagram.json (it hides new IG posts).
    return { ...api, media: { data: posts } };
  }
  return { ...api, media: { data } };
}

const Count = ({ value, label }) => {
  const { ref, inView } = useInView();
  const controls = useAnimation();
  const [count, setCount] = useState(0);
  const targetCount = parseInt(value.replace(/[^\d]/g, ""), 10);

  useEffect(() => {
    if (inView) {
      const interval = setInterval(() => {
        if (count < targetCount) {
          setCount((prevCount) => prevCount + Math.ceil(targetCount / 50));
        } else {
          clearInterval(interval);
        }
      }, 20);
      return () => clearInterval(interval);
    }
  }, [inView, count, targetCount]);

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
        },
      });
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={controls}
      className="count"
    >
      {count.toLocaleString()}
      <div className="reach">{label}</div>
    </motion.div>
  );
};

export const Gallery = () => {
  const { data } = useQuery(gql(LIST_INSTAGRAM_POSTS), {
    // Show cache immediately, then refresh so new IG posts appear after backend sync.
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-and-network",
    errorPolicy: "ignore",
  });

  const ig = useMemo(() => {
    const api = data?.listInstagramPost;
    const fallback = instagramList?.data?.listInstagramPost;
    const fallbackCount = fallback?.media?.data?.length ?? 0;
    // Always prefer AppSync when present so header counts stay in sync with Dynamo/IG (not bundled JSON).
    if (api != null) {
      return mergeStableInstagramMedia(api, fallback);
    }
    if (fallbackCount > 0) return fallback;
    return fallback ?? null;
  }, [data]);

  return (
    <>
      <div className="w-full min-h-[88px] lg:min-h-[122px] flex-col flex flex-wrap gap-[23.24px] md:gap-x-[20px] md:!flex-row justify-center items-center bg-[#9C765B]">
        <div className="w-auto 2xl:max-w-[800px] md:flex flex-wrap gap-[23.24px] md:gap-[30px] items-center">
          <div className="flex justify-center items-center h-[100px] md:h-auto">
            <div className="justify-center items-center hidden lg:block">
              <img src={InstagramIcon} alt="insta" className="m-2 md:mr-2 xl:mr-0 md:m-0 w-[82px] h-[83px]" />
            </div>
            <div className="instagram_header_logo flex justify-center items-center md:ml-[68px] mr-[16px]">
              <SaddlefitIcon className="!w-[41px] !h-[41px] lg:!w-[83px] lg:!h-[82px]" />
            </div>
            <div className="h-[60px] flex">
              <div className="mt-3">
                <h2 className="main_heading">SADDLEFIT</h2>
                <h3 className="email_saddle">@saddlefit.io</h3>
              </div>
              <div className="block lg:hidden">
                <img
                  alt="insta-mobile"
                  src={InstagramIconMobile}
                  className="w-[18.411px] h-[18.411px] mt-3.5 ml-2"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-center relative items-center mx-auto w-[300px] gap-x-[40px]">
            <div>
              <Count value={`${ig?.media_count ?? 0}`} label="Post" />
            </div>
            <div>
              <Count value={`${ig?.followers_count ?? 0}`} label="Followers" />
            </div>
            <div>
              <Count value={`${ig?.follows_count ?? 0}`} label="Following" />
            </div>
          </div>
        </div>
        <div className="mt-[-25px] md:mt-0">
          <Button
            onClick={() =>
              window.open("https://www.instagram.com/saddlefit.io")
            }
            className="follow_btn mx-auto my-5 md:my-0 md:mx-0 !h-[40px] !w-[87px]"
          >
            Follow
          </Button>
        </div>
      </div>
      <EmbedPost media={ig?.media} />
    </>
  );
};
